import { inject, Injector, runInInjectionContext } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { initializePurlyService } from './purly.initializer';
import { loadRuntimeConfig } from './runtime-config.initializer';

/**
 * Angular APP_INITIALIZER factory function that sequences runtime config loading
 * and Purly service initialization.
 *
 * These two initializers must run sequentially — Purly initialization reads from
 * RuntimeConfigService, which is only populated after loadRuntimeConfig completes.
 * Combining them in a single initializer guarantees the execution order regardless
 * of how Angular resolves concurrent APP_INITIALIZER entries.
 *
 * INITIALIZATION ORDER:
 * 1. loadRuntimeConfig() — fetches assets/runtime-config.json into RuntimeConfigService
 * 2. initializePurlyService() — reads from RuntimeConfigService and initializes Purly
 *
 * @returns Factory function that returns a Promise for Angular's APP_INITIALIZER
 */
export function initializeRuntimeSequence(): () => Promise<void> {
  const injector = inject(Injector);
  const logger = inject(NGXLogger);
  const runtimeConfigFn = loadRuntimeConfig();

  return async () => {
    logger.trace('[JmkwModule] Starting runtime initialization sequence');

    try {
      await runtimeConfigFn();
      logger.trace('[JmkwModule] ✅ Runtime config loader completed');

      await runInInjectionContext(injector, () => {
        const purlyInitializer = initializePurlyService();
        return purlyInitializer();
      });
    } catch (error) {
      logger.error('[JmkwModule] ❌ Initialization sequence failed:', error);
      // Don't re-throw — allow app to continue with degraded functionality
    }
  };
}
